import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { policyClient } from 'apis';
import { OverViewViewModel, TotalValuePerMonth } from 'apis/nswag';

import { sortPoliciesByAccountLabel } from 'features/users/components/utils';

import { ChartRange } from './charts/utils';

export interface ClientOverviewState {
  data?: OverViewViewModel;
  loading: boolean;
  error: string | null;
  showContribution: boolean;
  selectedPolicyId: string;
  range: ChartRange;
  requestedYear: number | null;
  requestedMonth: number;
}

export const initialState: ClientOverviewState = {
  data: undefined,
  loading: false,
  error: null,
  showContribution: false,
  selectedPolicyId: 'total',
  range: 'T12',
  requestedYear: null,
  requestedMonth: 0,
};

interface FetchOverviewDataParams {
  month: number;
  year: number;
  userId: string;
  clientId: number | null;
}

export const fetchOverviewData = createAsyncThunk(
  'clientOverview/fetchOverviewData',
  async ({ month, year, userId, clientId }: FetchOverviewDataParams, { rejectWithValue }) => {
    try {
      const result = await policyClient.getHistoryPoints(month, year, userId, clientId);
      return result.toJSON() as OverViewViewModel; // HACK: we only need plain data
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch overview data');
    }
  }
);

const clientOverviewSlice = createSlice({
  name: 'clientOverview',
  initialState,
  reducers: {
    clearOverviewData: (state) => {
      state.data = undefined;
      state.error = null;
    },
    setShowContribution: (state, action: PayloadAction<boolean>) => {
      state.showContribution = action.payload;
    },
    setSelectedPolicyId: (state, action: PayloadAction<string>) => {
      state.selectedPolicyId = action.payload;
    },
    setRange: (state, action: PayloadAction<ChartRange>) => {
      state.range = action.payload;
    },
    setRequestedYear: (state, action: PayloadAction<number | null>) => {
      state.requestedYear = action.payload;
    },
    setMonthAndYear: (state, action: PayloadAction<{ month: number; year: number }>) => {
      state.requestedMonth = action.payload.month;
      state.requestedYear = action.payload.year || state.requestedYear;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOverviewData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOverviewData.fulfilled, (state, action: PayloadAction<OverViewViewModel>) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchOverviewData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearOverviewData,
  setRange,
  setRequestedYear,
  setMonthAndYear,
  setSelectedPolicyId,
  setShowContribution,
} = clientOverviewSlice.actions;

// Base selectors
export const selectOverviewData = (state: { clientOverview: ClientOverviewState }) => state.clientOverview.data;
export const selectLoading = (state: { clientOverview: ClientOverviewState }) => state.clientOverview.loading;
export const selectError = (state: { clientOverview: ClientOverviewState }) => state.clientOverview.error;
export const selectShowContribution = (state: { clientOverview: ClientOverviewState }) =>
  state.clientOverview.showContribution;
export const selectSelectedPolicyId = (state: { clientOverview: ClientOverviewState }) =>
  state.clientOverview.selectedPolicyId;
export const selectRange = (state: { clientOverview: ClientOverviewState }) => state.clientOverview.range;
export const selectRequestedYear = (state: { clientOverview: ClientOverviewState }) =>
  state.clientOverview.requestedYear;
export const selectMonth = (state: { clientOverview: ClientOverviewState }) => state.clientOverview.requestedMonth;
export const selectYear = (state: { clientOverview: ClientOverviewState }) => {
  const requestedYear = selectRequestedYear(state);
  const lastValuationYear = Number((state.clientOverview.data?.lastValuationDate ?? '').slice(0, 4));
  const currentYear = new Date().getFullYear();

  return requestedYear ?? (lastValuationYear || currentYear);
};

// Derived selectors
export const selectPolicies = (state: { clientOverview: ClientOverviewState }) => {
  const data = state.clientOverview.data;
  return data?.policies ? sortPoliciesByAccountLabel(data.policies) : [];
};

export const selectDeathBenefits = (state: { clientOverview: ClientOverviewState }) => {
  const data = state.clientOverview.data;
  return data?.deathBenefits ? sortPoliciesByAccountLabel(data.deathBenefits) : [];
};

export const selectTotalContributeChart = (state: { clientOverview: ClientOverviewState }) => {
  return state.clientOverview.data?.planContributions ?? new TotalValuePerMonth();
};

export const selectNetAccumulatedValues = (state: { clientOverview: ClientOverviewState }) => {
  return state.clientOverview.data?.netAccumulatedValues ?? new TotalValuePerMonth();
};

export const selectMinYear = (state: { clientOverview: ClientOverviewState }) => {
  return state.clientOverview.data?.minYearSelected ?? 0;
};

export const selectLastValuationDate = (state: { clientOverview: ClientOverviewState }) => {
  return state.clientOverview.data?.lastValuationDate ?? '';
};

export const selectModel = (state: { clientOverview: ClientOverviewState }) => state.clientOverview.data;

export const selectClientDetail = (state: { clientOverview: ClientOverviewState }) => {
  const policies = selectPolicies(state);

  if (policies.length === 0) {
    return {
      name: '',
      employerName: '',
      planName: '',
      totalContributions: 0,
      totalPlanValue: 0,
      totalPlanContributionValue: 0,
      totalNetAccumulatedValues: 0,
    };
  }

  const firstPolicy = policies[0];
  const name = firstPolicy.clientName || '';
  const employerName = firstPolicy.employerName || '';
  const planName = firstPolicy.planName || '';

  let totalContributions = 0;
  let totalPlanContributionValue = 0;
  let totalPlanValue = 0;
  let totalNetAccumulatedValues = 0;

  policies.forEach((policy) => {
    if (policy.exchangedPolicyForId === null && policy.historyPoint) {
      totalContributions += policy.totalContributionValue ?? 0;
      totalPlanContributionValue += policy.planContributionValue ?? 0;
      totalPlanValue += policy.totalPlanValue ?? 0;
      const net = (policy.historyPoint.accumulatedValue ?? 0) - (policy.historyPoint.loanBalance ?? 0);
      totalNetAccumulatedValues += net;
    }
  });

  return {
    name,
    employerName,
    planName,
    totalContributions,
    totalPlanValue,
    totalPlanContributionValue,
    totalNetAccumulatedValues,
  };
};

export default clientOverviewSlice.reducer;
